// Common
import {
  ComponentRef,
  Directive,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

// Directives
import { ContextMenuDirective } from '@frontendlib/components/features/popover/directives/context-menu.directive';
import type { PopoverPlacement } from '@frontendlib/components/features/popover/types/placement';
import type { PopoverTrigger } from '@frontendlib/components/features/popover/types/trigger';

// Components
import { StitchTypeContextMenuComponent } from '../components/stitch-type-context-menu/stitch-type-context-menu.component';

// Types
import { StateKey } from '@modules/settings/types/state-key';
import { KPListType } from '../types/kp-list-type';
import { getKPResultType, type KPResultCounts } from '../types/kp-result-types';

// RX
import { take, takeUntil } from 'rxjs/operators';

// Services
import { StateService } from '@modules/settings/services/state.service';

declare module '@modules/settings/types/state' {
  export interface State {
    [StateKey.kpResultsStitchType]?: KPListType;
  }
}

@Directive({
  selector: '[appKPStitchTypeContextMenu]',
})
export class StitchTypeContextMenuDirective extends ContextMenuDirective implements OnInit, OnChanges {
  // Inputs
  @Input() stchPopoverTrigger: PopoverTrigger = 'click';
  @Input() stchPopoverPlacement: PopoverPlacement = 'bottomLeft';
  @Input() stchPopoverNoBorder = true;
  @Input() appKPStitchTypeContextMenu: StateKey;
  /** Handed on to the open menu whenever it changes, so the menu's counts stay live. */
  @Input() appKPStitchTypeContextMenuCounts: KPResultCounts = {};

  // Outputs
  @Output() appKPStitchTypeContextMenuOpened = this.contextMenuOpened;
  @Output() appKPStitchTypeContextMenuChange = new EventEmitter<KPListType>();

  // Private
  private value: KPListType = KPListType.message;
  private menuRef?: ComponentRef<StitchTypeContextMenuComponent>;

  constructor(
    private stateService: StateService,
    injector: Injector,
  ) {
    super(injector);
  }

  /**
   * Context Menu Interface
   */

  registerInstance(componentRef: ComponentRef<StitchTypeContextMenuComponent>): void {
    this.menuRef = componentRef;
    componentRef.onDestroy(() => {
      if (this.menuRef === componentRef) {
        this.menuRef = undefined;
      }
    });
    componentRef.setInput('value', this.value);
    componentRef.setInput('counts', this.appKPStitchTypeContextMenuCounts);

    componentRef.instance.typeChange.subscribe((value: KPListType) => {
      this.value = value;
      this.appKPStitchTypeContextMenuChange.emit(this.value);
      componentRef.setInput('value', this.value);
      this.stateService.setState(this.appKPStitchTypeContextMenu, this.value);
    });
  }

  registerComponent() {
    return StitchTypeContextMenuComponent;
  }

  /**
   * Lifecycle
   */

  override ngOnInit() {
    super.ngOnInit();
    // The menu pads itself.
    this.stchPopoverCustomStyles = { padding: '0' };
    this.stateService
      .getState(this.appKPStitchTypeContextMenu)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((filterKey: KPListType) => {
        // A saved type without a result list (one since removed) would leave the panel with no section.
        this.value = getKPResultType(filterKey)?.type ?? KPListType.message;
        this.appKPStitchTypeContextMenuChange.emit(this.value);
      });
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if ('appKPStitchTypeContextMenuCounts' in changes) {
      this.menuRef?.setInput('counts', this.appKPStitchTypeContextMenuCounts);
    }
  }
}
